import { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { RiCloseCircleFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import optimalIcon from "../../dist/images/diamond.svg";
import secureIcon from "../../dist/images/secure.svg";
import { reserveVehicleInspectionSlot } from "../../redux/actions/reservation_actions";
import CustomToastNotification from "../common/CustomToastNotification";
import FormField from "../common/FormField";
import { carYears, makesAndModels } from "../RentVehicleSteps/data";

const ReserveSlotModal = ({ show, onHide, planType }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.reservation_reducers.loading);
  const applicationProgress = useSelector(
    (state) => state.application_reducers.applicationProgress
  );

  const [carOwnershipStatus, setCarOwnershipStatus] = useState("");

  const {
    reset,
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm();

  // Pre-fill make & model and year from step1 selection only on /rent-vehicle
  useEffect(() => {
    if (show) {
      const isRentVehiclePage = window.location.pathname === "/rent-vehicle";
      if (isRentVehiclePage && applicationProgress?.step1) {
        const { vehicleMake, vehicleModel, vehicleManufactureYear } =
          applicationProgress.step1;
        if (vehicleMake && vehicleModel) {
          const makeModel = `${vehicleMake} ${vehicleModel}`;
          if (makesAndModels.includes(makeModel)) {
            setValue("carMakeModel", makeModel);
          }
        }
        if (vehicleManufactureYear) {
          setValue("yearOfManufacture", vehicleManufactureYear);
        }
      }
    }
  }, [show, applicationProgress, setValue]);

  // Reset form and state when modal closes
  useEffect(() => {
    if (!show) {
      setCarOwnershipStatus("");
      reset();
    }
  }, [show, reset]);

  const submitForm = async (formData) => {
    if (!carOwnershipStatus) {
      return;
    }

    const data = {
      ...formData,
      planType: planType.toLowerCase(),
      carOwnershipStatus
    };

    // If user intends to buy, remove car make/model fields
    if (carOwnershipStatus !== "have_car") {
      delete data.carMakeModel;
      delete data.yearOfManufacture;
    }

    try {
      const response = await dispatch(reserveVehicleInspectionSlot(data));
      if (response) {
        toast.success(CustomToastNotification, {
          data: {
            title: "Slot reserved successfully!",
            content:
              "We'll call you within 48 hours to schedule your vehicle inspection."
          },
          ariaLabel: "Slot reserved successfully"
        });
        reset();
        setCarOwnershipStatus("");
        onHide();
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.[0] || err.message || "An error occurred";
      toast.error(CustomToastNotification, {
        data: {
          title: "Failed to reserve slot!",
          content: errorMessage
        },
        ariaLabel: "Failed to reserve slot",
        theme: "colored"
      });
    }
  };

  return (
    <ModalWrapper show={show} size="lg">
      <div className="px-1 pt-1 text-right">
        <RiCloseCircleFill
          onClick={() => onHide()}
          className="icon-30 text-medium-dark"
        />
      </div>
      <div className="plan_type rounded-end-4 d-flex justify-content-end align-items-center gap-2 px-4 py-2">
        <img
          src={planType.toLowerCase() === "secure" ? secureIcon : optimalIcon}
          alt={planType.toLowerCase()}
          className="width-30"
        />
        <h6 className="font-semibold font-18 text-lighter-black mt-2">
          {planType} Rental Plan
        </h6>
      </div>

      <div className="p-4 px-md-5">
        <h5 className="mb-3 font-25 text-light-dark font-semibold">
          Get Started
        </h5>
        <p className="text-maroon">
          To retain quality, we have limited slots each month. All vehicles must
          pass inspection before onboarding. Reserve your slot and get onboarded
          first.
        </p>

        <Form onSubmit={handleSubmit(submitForm)}>
          <FormField
            label="Full Name"
            error={errors.fullName}
            controlId="fullName"
          >
            <Form.Control
              type="text"
              name="fullName"
              placeholder="Your Name"
              {...register("fullName", {
                required: "Name is required",
                setValueAs: (v) => v.trim()
              })}
              className={`height-50 ${
                errors.fullName ? "border-red" : "border-gray"
              }`}
            />
          </FormField>
          <FormField
            label="Phone Number"
            error={errors.phoneNumber}
            controlId="phoneNumber"
          >
            <Form.Control
              type="tel"
              name="phoneNumber"
              placeholder="Your Phone Number"
              {...register("phoneNumber", {
                required: "Phone number is required"
              })}
              className={`height-50 ${
                errors.phoneNumber ? "border-red" : "border-gray"
              }`}
            />
          </FormField>

          {/* Q: Do you currently have the car or intend to buy? */}
          <div className="mb-4">
            <Form.Label>
              Do you currently have the car or intend to buy?
            </Form.Label>
            <div className="d-flex gap-3 mt-2">
              <CarOptionButton
                type="button"
                $isActive={carOwnershipStatus === "have_car"}
                onClick={() => setCarOwnershipStatus("have_car")}
              >
                I have the car
              </CarOptionButton>
              <CarOptionButton
                type="button"
                $isActive={carOwnershipStatus === "intend_to_buy"}
                onClick={() => setCarOwnershipStatus("intend_to_buy")}
              >
                I intend to buy
              </CarOptionButton>
            </div>
            {!carOwnershipStatus && (
              <p className="text-danger font-14 mt-2">
                Please select one option above
              </p>
            )}
          </div>

          {/* Only show car make/model and year if user has the car */}
          {carOwnershipStatus === "have_car" && (
            <>
              <FormField
                label="Car Make & Model"
                error={errors.carMakeModel}
                controlId="carMakeModel"
              >
                <Form.Control
                  as="select"
                  name="carMakeModel"
                  {...register("carMakeModel", {
                    required:
                      carOwnershipStatus === "have_car"
                        ? "Car make & model is required"
                        : false
                  })}
                  className={`height-50 ${
                    errors.carMakeModel ? "border-red" : "border-gray"
                  }`}
                >
                  <option value="">Select Make & Model</option>
                  {makesAndModels.map((make, index) => (
                    <option value={make} key={index}>
                      {make}
                    </option>
                  ))}
                </Form.Control>
              </FormField>
              <FormField
                label="Year of Manufacture"
                error={errors.yearOfManufacture}
                controlId="yearOfManufacture"
              >
                <Form.Control
                  as="select"
                  name="yearOfManufacture"
                  {...register("yearOfManufacture", {
                    required:
                      carOwnershipStatus === "have_car"
                        ? "Year of manufacture is required"
                        : false
                  })}
                  className={`height-50 ${
                    errors.yearOfManufacture ? "border-red" : "border-gray"
                  }`}
                >
                  <option value="">Select Year</option>
                  {carYears.map((year, index) => (
                    <option value={year} key={index}>
                      {year}
                    </option>
                  ))}
                </Form.Control>
              </FormField>
            </>
          )}

          <button
            type="submit"
            className="btn btn-dark br-10 w-100 mt-4"
            disabled={isLoading || !carOwnershipStatus}
          >
            {isLoading ? "Reserving..." : "Reserve My Slot"}
          </button>
          <p className="text-gray font-16 text-center pt-3">
            We'll call you within 24 hours to schedule your vehicle inspection
          </p>
        </Form>
      </div>
    </ModalWrapper>
  );
};

export default ReserveSlotModal;

const CarOptionButton = styled.button`
  padding: 0.6rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.2s ease;
  border: 2px solid
    ${(props) => (props.$isActive ? "var(--green, #28a745)" : "#c4c4c4")};
  background-color: ${(props) =>
    props.$isActive ? "var(--green, #28a745)" : "#fff"};
  color: ${(props) => (props.$isActive ? "#fff" : "#333")};

  &:hover {
    border-color: var(--green, #28a745);
    opacity: 0.9;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
  }
`;

const ModalWrapper = styled(Modal)`
  .modal-content {
    max-width: 700px;
    margin: auto;
    border: none;
    border-radius: 10px;
    overflow: auto;
  }

  .plan_type {
    background-color: #fcf2c2;
    max-width: 285px;
  }
`;
